import { useEffect, useCallback, useRef } from 'react'
import { useUserStore } from '@/store/user'
import {
  subscribeToMercureTopic,
  closeNativeEventSource,
} from '@/lib/helpers/subscribeToMercureTopic.ts'
import useEventTopicsStore from '@/store/eventTopics.ts'
import createIri from '@/lib/helpers/createIri.ts'
import { isNativePlatform } from '@/lib/platform/platformDetection'

function useEventSubscriber(
  topic: string,
  handler: (data: MessageEvent) => void,
  useCurrentOrganization: boolean = false,
) {
  const currentOrganizationMembership = useUserStore(state => state.currentOrganizationMembership)
  const removeTopic = useEventTopicsStore(state => state.removeTopic)
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  const subscribe = useCallback(() => {
    let composedTopic = topic
    // console.log(composedTopic, 'composed topic 🚀')
    if (useCurrentOrganization && currentOrganizationMembership) {
      const orgIri = createIri('organizations', currentOrganizationMembership.organization?.id)
      composedTopic = `${orgIri}${topic}`
    }

    const eventSource = subscribeToMercureTopic(composedTopic, event => {
      handlerRef.current(event)
    })

    return { eventSource, composedTopic }
  }, [topic, currentOrganizationMembership, useCurrentOrganization])

  useEffect(() => {
    // Skip SSE subscription for invalid topics (e.g. public preview mode passes '/issues/null')
    if (topic.includes('/null')) return

    let eventSource: EventSource | null = null
    let composedTopic: string | null = null

    const subscribeAndSet = () => {
      const result = subscribe()
      eventSource = result.eventSource
      composedTopic = result.composedTopic
    }

    subscribeAndSet()

    const cleanupEventSource = () => {
      if (eventSource) {
        eventSource.close()
        removeTopic(composedTopic || '')
      } else if (isNativePlatform() && composedTopic) {
        // On native (iOS/Android), EventSource is stored in a Map, not returned
        closeNativeEventSource(composedTopic)
        removeTopic(composedTopic)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        cleanupEventSource()
        subscribeAndSet() // Re-subscribe
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cleanupEventSource()
    }
  }, [subscribe, removeTopic, topic]) // removed handler and others
}

export default useEventSubscriber
